import { useCallback, useEffect, useRef, useState } from "react";

import { formatDateTimeLocal } from "./format";
import { fetchGuardrailMetrics } from "./guardrail-metrics-api";
import type { GuardrailEvent, GuardrailMetricsSummary } from "./guardrail-metrics-types";

export interface GuardrailMetricsFilters {
  startTime: string;
  endTime: string;
  environment: string;
  policy: string;
}

export interface UseGuardrailMetricsReturn {
  summary: GuardrailMetricsSummary | null;
  events: GuardrailEvent[];
  isLoading: boolean;
  error: string | null;
  filters: GuardrailMetricsFilters;
  setFilters: (filters: GuardrailMetricsFilters) => void;
  search: () => void;
  selectedEvent: GuardrailEvent | null;
  selectEvent: (event: GuardrailEvent | null) => void;
}

function buildInitialFilters(): GuardrailMetricsFilters {
  const now = new Date();
  const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  return {
    startTime: formatDateTimeLocal(sevenDaysAgo),
    endTime: formatDateTimeLocal(now),
    environment: "",
    policy: "",
  };
}

export function useGuardrailMetrics(): UseGuardrailMetricsReturn {
  const initialFilters = useRef(buildInitialFilters());
  const [summary, setSummary] = useState<GuardrailMetricsSummary | null>(null);
  const [events, setEvents] = useState<GuardrailEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<GuardrailEvent | null>(null);
  const [filters, setFilters] = useState<GuardrailMetricsFilters>(() => initialFilters.current);

  const loadMetrics = useCallback(async (currentFilters: GuardrailMetricsFilters) => {
    setIsLoading(true);
    setError(null);
    setSelectedEvent(null);
    try {
      const result = await fetchGuardrailMetrics({
        startTime: new Date(currentFilters.startTime).toISOString(),
        endTime: new Date(currentFilters.endTime).toISOString(),
        ...(currentFilters.environment ? { environment: currentFilters.environment } : {}),
        ...(currentFilters.policy ? { policy: currentFilters.policy } : {}),
        limit: 100,
      });
      setSummary(result.summary);
      setEvents(result.events);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load guardrail metrics");
      setSummary(null);
      setEvents([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const search = useCallback(() => {
    void loadMetrics(filters);
  }, [filters, loadMetrics]);

  const didMount = useRef(false);
  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      void loadMetrics(initialFilters.current);
    }
  }, [loadMetrics]);

  return {
    summary,
    events,
    isLoading,
    error,
    filters,
    setFilters,
    search,
    selectedEvent,
    selectEvent: setSelectedEvent,
  };
}
